import { useState } from 'react';

import type { UserResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  ErrorState,
  Form,
  FormError,
  Panel,
  SelectField,
  SubmitButton,
  TextField,
  fieldErrors,
  useNotifications,
  type Column,
} from '../../components';
import { useCreateUser, useDeactivateUser, useUsers } from '../queries';
import { GROUPS_COPY } from './groups';
import {
  EMPTY_USER_FORM,
  ONBOARDING_COPY,
  USER_ROLE_OPTIONS,
  type UserFormState,
} from './onboarding';
import {
  EMAIL_TAKEN_FIELD,
  EMAIL_TAKEN_FORM,
  ROLE_MEANINGS,
  deactivationQuestion,
  isEmailTaken,
  roleLabel,
} from './staffUsers';

/**
 * Where the people a panel lists and creates are registered (#391): one
 * company, or a whole company group.
 */
export type UsersScope =
  | { readonly kind: 'company'; readonly companyId: string }
  | { readonly kind: 'group'; readonly groupId: string };

/**
 * The people registered at one scope: who of them signs in, with which
 * permission, and how an access is ended -- the panel on a company's detail and
 * on a group's.
 *
 * One component rather than two copies because it is one act in two places,
 * and the second copy would go on passing its own tests while drifting away
 * from the first. What the scope changes is only how the request is narrowed:
 *
 *  - **A company** reads and creates through the `X-Company-Id` header the
 *    address already carries. The company is not on the body and cannot be,
 *    which is what makes the form structurally unable to create somebody in
 *    another company.
 *  - **A group** reads with `?group_id=` and creates with `group_id` in the
 *    body and *no* company header -- the server refuses both at once. The list
 *    is narrowed to `group_id` again on this side, so a person registered at
 *    one of the group's companies never reads as a group user here.
 *
 * Only the two customer roles are offered either way: a vyttech role belongs to
 * no company and no group. See [`StaffUsersPage`](./StaffUsersPage.tsx) for the
 * staff-wide list, where the vyttech roles are created.
 */
export function UsersPanel({ scope }: { scope: UsersScope }) {
  const companyId = scope.kind === 'company' ? scope.companyId : null;
  const groupId = scope.kind === 'group' ? scope.groupId : null;
  const users = useUsers(companyId, groupId);
  const createUser = useCreateUser();
  const deactivateUser = useDeactivateUser();
  const { notify } = useNotifications();

  const copy = scope.kind === 'company' ? COMPANY_PANEL_COPY : GROUP_PANEL_COPY;
  const visible = (items: readonly UserResponse[]) =>
    groupId === null ? items : items.filter((user) => (user.group_id ?? null) === groupId);

  // The inline creation form. Open on demand, the same discipline as the
  // contract edit: a form sitting open over the answer invites typing into it
  // by accident, and one that closes after submitting says "saved, and this is
  // what the server now says".
  const [userDraft, setUserDraft] = useState<UserFormState | null>(null);

  const userErrors = fieldErrors(createUser.error);
  // A 422 names the field; a bare 409 does not (it comes from the database's
  // unique index), so the screen supplies the field itself -- the same reading
  // `NewStaffUserPage` makes of the same response.
  const emailTaken = isEmailTaken(createUser.error);
  const emailError = userErrors['email'] ?? (emailTaken ? EMAIL_TAKEN_FIELD : undefined);

  const startCreatingUser = () => {
    setUserDraft(EMPTY_USER_FORM);
    createUser.reset();
  };

  const stopCreatingUser = () => {
    setUserDraft(null);
    createUser.reset();
  };

  const submitCreateUser = () => {
    if (userDraft === null) {
      return;
    }
    createUser.mutate(
      {
        user: {
          full_name: userDraft.fullName.trim(),
          email: userDraft.email.trim(),
          password: userDraft.password,
          role: userDraft.role,
          ...(groupId !== null && { group_id: groupId }),
        },
        // `null` for a group: its user travels with `group_id` in the body,
        // and a company header beside it is a refusal.
        companyId,
      },
      {
        onSuccess: (created) => {
          setUserDraft(null);
          notify('success', `${created.full_name} agora pode entrar.`);
        },
      },
    );
  };

  const deactivate = (user: UserResponse) => {
    // A mesma pergunta que a lista de pessoas da equipe faz, porque é a mesma
    // coisa que acontece com a pessoa depois do sim.
    if (!window.confirm(deactivationQuestion(user.email))) {
      return;
    }
    deactivateUser.mutate(
      // The row's own company rather than the page's -- the two are the same
      // on a company page, and a group user has none, so the request goes
      // without a header. A filter is not a fact about the person.
      { userId: user.id, companyId: user.company_id },
      {
        onSuccess: () => notify('success', ONBOARDING_COPY.deactivatedUser),
      },
    );
  };

  const userColumns: readonly Column<UserResponse>[] = [
    { key: 'full_name', header: ONBOARDING_COPY.userNameLabel, render: (user) => user.full_name },
    { key: 'email', header: ONBOARDING_COPY.userEmailLabel, render: (user) => user.email },
    // `roleLabel` rather than the narrowed list: the two the form offers are
    // all this scope can hold, but a row is whatever the server sent, and a
    // label is not the place to discover that.
    { key: 'role', header: ONBOARDING_COPY.userRoleLabel, render: (user) => roleLabel(user.role) },
    {
      key: 'is_active',
      header: ONBOARDING_COPY.userStatusLabel,
      render: (user) =>
        user.is_active ? (
          ONBOARDING_COPY.userActive
        ) : (
          // Still on the list, and said plainly. A row that merely disappeared
          // would leave "was this person removed, or did I imagine them?" as a
          // question nobody can answer.
          <span className="table__muted">{ONBOARDING_COPY.userInactive}</span>
        ),
    },
    {
      key: 'actions',
      header: ONBOARDING_COPY.actionsLabel,
      align: 'end',
      render: (user) => {
        if (!user.is_active) {
          return <span className="table__muted">—</span>;
        }
        return (
          <button
            type="button"
            className="button button--danger"
            disabled={deactivateUser.isPending}
            onClick={() => deactivate(user)}
          >
            {ONBOARDING_COPY.deactivateUser}
          </button>
        );
      },
    },
  ];

  return (
    <>
      <Panel title={copy.title} description={copy.description}>
        {/* The row goes with the button. An empty `panel__actions` still
            carries its bottom margin, so leaving it behind opens a gap above
            the list every time somebody opens the form. */}
        {userDraft === null && (
          <div className="panel__actions">
            <button type="button" className="button button--primary" onClick={startCreatingUser}>
              {ONBOARDING_COPY.addUser}
            </button>
          </div>
        )}

        <AsyncSection
          state={users}
          loadingLabel={copy.loading}
          errorTitle={copy.error}
          isEmpty={(data) => visible(data.items).length === 0}
          emptyTitle={copy.empty}
          emptyDescription={copy.emptyDescription}
        >
          {(data) => (
            <DataTable
              id={scope.kind === 'company' ? 'company-users' : 'group-users'}
              caption={copy.caption}
              columns={userColumns}
              rows={visible(data.items)}
              rowKey={(user) => user.id}
            />
          )}
        </AsyncSection>

        {/* Uma desativação recusada não tem onde cair dentro da tabela, e some
            se for só um toast. Fica aqui, abaixo da lista onde a ação foi
            pedida, com o código de referência que o suporte pede. */}
        {deactivateUser.isError && (
          <ErrorState error={deactivateUser.error} title={ONBOARDING_COPY.deactivateRefused} />
        )}
      </Panel>

      {userDraft !== null && (
        <Panel title={ONBOARDING_COPY.addUser} description={ONBOARDING_COPY.addUserDescription}>
          <Form onSubmit={submitCreateUser} aria-label={ONBOARDING_COPY.addUser}>
            <FormError
              error={createUser.error}
              {...(emailTaken && { message: EMAIL_TAKEN_FORM })}
            />

            <TextField
              label={ONBOARDING_COPY.userFullNameLabel}
              value={userDraft.fullName}
              onChange={(value) => setUserDraft({ ...userDraft, fullName: value })}
              required
              autoComplete="off"
              {...(userErrors['full_name'] !== undefined && { error: userErrors['full_name'] })}
            />

            <TextField
              label={ONBOARDING_COPY.userEmailLabel}
              type="email"
              value={userDraft.email}
              onChange={(value) => setUserDraft({ ...userDraft, email: value })}
              required
              hint={ONBOARDING_COPY.userEmailHint}
              autoComplete="off"
              {...(emailError !== undefined && { error: emailError })}
            />

            <TextField
              label={ONBOARDING_COPY.userPasswordLabel}
              type="password"
              value={userDraft.password}
              onChange={(value) => setUserDraft({ ...userDraft, password: value })}
              required
              hint={ONBOARDING_COPY.userPasswordHint}
              autoComplete="new-password"
              {...(userErrors['password'] !== undefined && { error: userErrors['password'] })}
            />

            <SelectField
              label={ONBOARDING_COPY.userRoleLabel}
              value={userDraft.role}
              onChange={(chosen) => {
                if (chosen === '') {
                  return;
                }
                setUserDraft({ ...userDraft, role: chosen as UserFormState['role'] });
              }}
              options={USER_ROLE_OPTIONS}
              required
              hint={ROLE_MEANINGS[userDraft.role]}
              {...(userErrors['role'] !== undefined && { error: userErrors['role'] })}
            />

            <p className="field__hint">{copy.scopeNote}</p>

            <div className="form__actions">
              <SubmitButton
                pending={createUser.isPending}
                pendingLabel={ONBOARDING_COPY.addingUser}
              >
                {ONBOARDING_COPY.addUser}
              </SubmitButton>
              <button type="button" className="button button--ghost" onClick={stopCreatingUser}>
                {ONBOARDING_COPY.cancelUser}
              </button>
            </div>
          </Form>
        </Panel>
      )}
    </>
  );
}

/** What the panel is about, per scope. Everything else is read the same way. */
interface PanelCopy {
  readonly title: string;
  readonly description: string;
  readonly loading: string;
  readonly error: string;
  readonly empty: string;
  readonly emptyDescription: string;
  readonly caption: string;
  readonly scopeNote: string;
}

const COMPANY_PANEL_COPY: PanelCopy = {
  title: ONBOARDING_COPY.usersTitle,
  description: ONBOARDING_COPY.usersDescription,
  loading: ONBOARDING_COPY.usersLoading,
  error: ONBOARDING_COPY.usersError,
  empty: ONBOARDING_COPY.noUsers,
  emptyDescription: ONBOARDING_COPY.noUsersDescription,
  caption: ONBOARDING_COPY.usersCaption,
  scopeNote: ONBOARDING_COPY.userCompanyNote,
};

const GROUP_PANEL_COPY: PanelCopy = {
  title: GROUPS_COPY.usersTitle,
  description: GROUPS_COPY.usersDescription,
  loading: GROUPS_COPY.usersLoading,
  error: GROUPS_COPY.usersError,
  empty: GROUPS_COPY.noUsers,
  emptyDescription: GROUPS_COPY.noUsersDescription,
  caption: GROUPS_COPY.usersCaption,
  scopeNote: GROUPS_COPY.userScopeNote,
};
